import { createSelector } from 'reselect';
import { LoadState } from '@ally/use-data-domain';
/**
 * @author Matt Knapp <matthew.knapp@ally.com>
 * @author Jason Pollman <jason.pollman@ally.com>
 *
 * Allows conditional rendering of _anything_ based on the `loadState`
 * of the provided `domain`
 * @example
 * const cata = {
 *   unloaded: null,
 *   loading: <Spinner />,
 *   loaded: getAccounts(appState)
 *   failed: throw new Error('Account Data failed to load')
 * }
 *
 * const itemToRender = withLoadState(cata)(accountsDomain)
 */
export function withLoadState(cata) {
    var returnWrapper = function (domain) {
        var _a;
        var loadState = (_a = domain === null || domain === void 0 ? void 0 : domain.status) === null || _a === void 0 ? void 0 : _a.loadState;
        return cata[loadState] || cata[LoadState.UNLOADED];
    };
    return returnWrapper;
}
/**
 * Allows the conditional rendering of a given selector based on the
 * load state of the selected data. For `loaded`, the `data` returned
 * from the selector is passed as a parameter to the `loaded` function.
 * For `failed`, the `error` set for the particular domain in which
 * the selected data should exist is returned.
 * @example
 * const withAccountsLoadState = selectWithLoadState(getDataLoadState,
 * getError)
 *
 * // in consuming app:
 *
 * const cata = {
 *   unloaded: () => null,
 *   loading: () => <Spinner />,
 *   loaded: (data) => <Accounts data={data} />,
 *   failed: (error) => <ErrorMessage error={error} />,
 * }
 *
 * const renderAccounts = withAccountsLoadState(getAccounts)(appState)
 *
 * const AccountsDropdown = () => {
 *   return renderAccounts(cata)
 * }
 */
export function selectWithLoadState(loadStateSelector, errorSelector, isUpdatingSelector) {
    return function (selector) { return function (state) { return function (_a) {
        var unloaded = _a.unloaded, loading = _a.loading, loaded = _a.loaded, failed = _a.failed;
        var loadState = loadStateSelector(state);
        var error = errorSelector(state);
        var isUpdating = isUpdatingSelector === null || isUpdatingSelector === void 0 ? void 0 : isUpdatingSelector(state);
        var data = selector(state);
        switch (loadState) {
            case LoadState.UNLOADED:
                return unloaded();
            case LoadState.LOADING:
                return loading(isUpdating);
            case LoadState.LOADED:
                return loaded(data);
            case LoadState.FAILED:
                return failed(error);
            default:
                throw new Error(loadState + " is not a valid LoadState");
        }
    }; }; };
}
/**
 * Takes in a `Selector<S, Domain<T>>` and returns numerous selectors for interacting with that domain:
 * - `getDomainData: Selector<S, T | null>`
 * - `getDomainStatus: Selector<S, Status>`
 * - `getDomainLoadState: Selector<S, LoadState>`
 * - `getDomainIsUpdating: Selector<S, boolean | undefined>`
 * - `getDomainTimeStamp: Selector<S, Date>`
 * - `getDomainError: Selector<S, Error | null>`
 * -`getDomainResponse: Selector<S, Response | null>`
 * - `withDomainLoadState: WithDomainLoadState<S>`
 * @example
 * const getAccountsDomain: Selector<HostData, Domain<AccountsData>> = ({
 *   accounts,
 * }) => accounts
 *
 * const selectors = generateDomainSelectors(getAccountsDomain)
 * const data = selectors.getDomainData(state) // => AccountsData | null
 */
export function generateDomainSelectors(getDomain) {
    var getDomainData = createSelector(getDomain, function (_a) {
        var data = _a.data;
        return data;
    });
    var getDomainStatus = createSelector(getDomain, function (_a) {
        var status = _a.status;
        return status;
    });
    var getDomainLoadState = createSelector(getDomainStatus, function (_a) {
        var loadState = _a.loadState;
        return loadState;
    });
    var getDomainIsUpdating = createSelector(getDomainStatus, function (_a) {
        var isUpdating = _a.isUpdating;
        return isUpdating;
    });
    var getDomainTimeStamp = createSelector(getDomainStatus, function (_a) {
        var timeStamp = _a.timeStamp;
        return timeStamp;
    });
    var getDomainError = createSelector(getDomain, function (_a) {
        var error = _a.error;
        return error;
    });
    var getDomainResponse = createSelector(getDomain, function (_a) {
        var response = _a.response;
        return response;
    });
    var withDomainLoadState = selectWithLoadState(getDomainLoadState, getDomainError, getDomainIsUpdating);
    return {
        getDomainData: getDomainData,
        getDomainStatus: getDomainStatus,
        getDomainLoadState: getDomainLoadState,
        getDomainIsUpdating: getDomainIsUpdating,
        getDomainTimeStamp: getDomainTimeStamp,
        getDomainError: getDomainError,
        getDomainResponse: getDomainResponse,
        withDomainLoadState: withDomainLoadState,
    };
}
//# sourceMappingURL=domainSelectors.js.map